import { Component, DestroyRef, ElementRef, HostBinding, inject, input, Renderer2 } from '@angular/core';
import { injectDatePickerDateButton } from 'ng-primitives/date-picker';
import { InlineButtonComponent } from '../../../../atoms/inline-button/inline-button.component';

@Component({
  selector: 'joii-day',
  imports: [InlineButtonComponent],
  template: `
    <div class="relative">
      <!-- The host is the grid's roving tab stop (ngpDatePickerDateButton), so keep this out of the tab order. -->
      <button
        joii-inline-button
        class="day-button"
        tabindex="-1"
        [class.today]="today()"
        [variant]="'secondary'"
        [disabled]="disabled() || hostDisabled()"
        [transparent]="true"
        [borderless]="true"
        [active]="selected()"
      >
        {{ label() }}
      </button>
      @if (hasEvent()) {
        <div class="absolute bottom-0 items-center justify-center w-full flex flex-row">
          <svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none" class="">
            <circle cx="3" cy="3" r="3" class="event-dot" />
          </svg>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
    }
    .day-button {
      width: 100%;
      height: 100%;
      aspect-ratio: 1/1;
      min-width: 40px;
      min-height: 40px;
    }
    .day-button[data-state-active='true'] {
      background-color: var(--Vetai-Form_controls--Background_Active);
      border: 1px solid var(--Vetai-Form_controls--Border_Active);
      color: var(--Vetai-Form_controls--Border_Active);
      border-radius: var(--Vetai-Radius-100);
    }
    :host-context([data-show-today='true']) .day-button.today {
      color: var(--Vetai-Form_controls--Text_Active);
      border-radius: var(--Vetai-Radius-100);
      border: 1px solid var(--Vetai-Inline_links_and_info--Default);
    }
    .day-button[disabled] {
      background: transparent;
      color: var(--Vetai-Form_controls--Placeholder);
      cursor: not-allowed;
      pointer-events: none;
    }
    :host-context([data-show-today='true']) .day-button.today[disabled],
    :host-context([data-show-today='true']) .day-button.today[disabled][data-state-active='true'] {
      background: var(--Vetai-Backgrounds--Tint_2);
      color: var(--Vetai-Form_controls--Placeholder);
      border: 1px solid var(--Vetai-Backgrounds--Tint_2);
    }
    /* Dark Theme — match light mode: only emphasize “today” when showToday is on */
    :host-context([data-theme='dark']):host-context([data-show-today='true']) .day-button.today {
      color: var(--Vetai-Form_controls--Text_Hover);
      border: 1px solid var(--Vetai-Form_controls--Border_Active);
      background: var(--Vetai-Form_controls--Background_Active);
    }
    :host-context([data-theme='dark']):host-context([data-show-today='true'])
      .day-button.today[data-state-active='true'] {
      color: var(--Vetai-Inline_links_and_info--Active);
    }
    :host-context([data-theme='dark']) :host-context([data-show-today='true']) .day-button.today {
      border: none;
    }
    :host-context([data-theme='dark']) .day-button[disabled] {
      color: var(--Vetai-Backgrounds--Highlight);
    }
    :host-context([data-theme='dark']):host-context([data-show-today='true']) .day-button.today[disabled],
    :host-context([data-theme='dark']):host-context([data-show-today='true'])
      .day-button.today[disabled][data-state-active='true'] {
      color: var(--Vetai-Form_controls--Placeholder);
      background: var(--Vetai-Backgrounds--Tint_2);
      border: 1px solid var(--Vetai-Backgrounds--Tint_2);
    }
    .event-dot {
      fill: var(--Vetai-Inline_links_and_info--Default);
    }
    :host-context([data-variant='enableEvents']) .event-dot {
      fill: none !important;
    }
  `,
})
export class DayComponent {
  private dateButton = injectDatePickerDateButton();
  /**
   * When the inner button is disabled it uses pointer-events: none, so clicks hit the host and
   * would still bubble; block pointer events on the host for the same disabled state.
   */
  @HostBinding('style.pointer-events')
  get hostPointerEvents(): 'none' | 'auto' {
    return this.disabled() || this.hostDisabled() ? 'none' : 'auto';
  }

  label = input<string>('');
  selected = input<boolean>(false);
  today = input<boolean>(false);
  disabled = input<boolean>(false);
  hasEvent = input<boolean>(false);
  hostDisabled = this.dateButton.disabled;

  constructor() {
    // The ng-primitives date button on this host selects on click/Enter/Space and only knows about
    // its own disabled state (min/max/dateDisabled), not our `disabled` input (e.g. the
    // enableEvents variant). Listen in the capture phase so this runs before the primitive's
    // handler and swallow activation of a disabled day.
    const host = inject(ElementRef<HTMLElement>).nativeElement;
    const renderer = inject(Renderer2);
    const guard = (event: Event) => {
      if (!this.disabled()) return;
      if (event instanceof KeyboardEvent && event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    const unlisten = [
      renderer.listen(host, 'click', guard, { capture: true }),
      renderer.listen(host, 'keydown', guard, { capture: true }),
    ];
    inject(DestroyRef).onDestroy(() => unlisten.forEach((fn) => fn()));
  }
}
